import { useState, useEffect, useMemo, useRef } from "react";
import { isLikelyImageUrl } from "../utils/string";

// Global cache for dimensions
const dimensionsCache = new Map();

export const useMediaDimensions = (url) => {
  const cachedDimensions =
    url && dimensionsCache.has(url) ? dimensionsCache.get(url) : null;

  const [dimensions, setDimensions] = useState(
    () => cachedDimensions || { width: 0, height: 0 }
  );
  const [loading, setLoading] = useState(() => Boolean(url && !cachedDimensions));

  // Detect content type
  const isImage = useMemo(() => isLikelyImageUrl(url), [url]);

  // Calculate aspect ratio
  const aspectRatio = useMemo(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return 1;
    return dimensions.width / dimensions.height;
  }, [dimensions.width, dimensions.height]);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }

    // Check cache
    if (dimensionsCache.has(url)) {
      setDimensions(dimensionsCache.get(url));
      setLoading(false);
      return;
    }

    setLoading(true);

    if (isImage) {
      const img = new Image();

      const onLoadImage = () => {
        const newDimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
        dimensionsCache.set(url, newDimensions);
        setDimensions(newDimensions);
        setLoading(false);
      };

      const onErrorImage = () => {
        const defaultDimensions = { width: 1080, height: 1080 };
        dimensionsCache.set(url, defaultDimensions);
        setDimensions(defaultDimensions);
        setLoading(false);
      };

      img.onload = onLoadImage;
      img.onerror = onErrorImage;
      img.src = url;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    } else {
      const video = document.createElement("video");

      const onLoadedMetadataVideo = () => {
        const newDimensions = {
          width: video.videoWidth,
          height: video.videoHeight,
        };
        dimensionsCache.set(url, newDimensions);
        setDimensions(newDimensions);
        setLoading(false);
      };

      const onErrorVideo = () => {
        const defaultDimensions = { width: 1080, height: 1920 };
        dimensionsCache.set(url, defaultDimensions);
        setDimensions(defaultDimensions);
        setLoading(false);
      };

      video.onloadedmetadata = onLoadedMetadataVideo;
      video.onerror = onErrorVideo;
      video.src = url;
      video.preload = "metadata";

      return () => {
        video.onloadedmetadata = null;
        video.onerror = null;
        video.src = "";
      };
    }
  }, [url, isImage]);

  return { dimensions, aspectRatio, isImage, loading };
};

export const getMediaDimensionsAsync = (url) => {
  if (!url) {
    return Promise.resolve({ width: 0, height: 0 });
  }

  if (dimensionsCache.has(url)) {
    return Promise.resolve(dimensionsCache.get(url));
  }

  const isImage = isLikelyImageUrl(url);

  return new Promise((resolve) => {
    if (isImage) {
      const img = new Image();
      img.onload = () => {
        const dimensions = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
        dimensionsCache.set(url, dimensions);
        resolve(dimensions);
      };
      img.onerror = () => {
        const defaultDimensions = { width: 1080, height: 1080 };
        dimensionsCache.set(url, defaultDimensions);
        resolve(defaultDimensions);
      };
      img.src = url;
    } else {
      const video = document.createElement("video");
      video.onloadedmetadata = () => {
        const dimensions = {
          width: video.videoWidth,
          height: video.videoHeight,
        };
        dimensionsCache.set(url, dimensions);
        resolve(dimensions);
      };
      video.onerror = () => {
        const defaultDimensions = { width: 1080, height: 1920 };
        dimensionsCache.set(url, defaultDimensions);
        resolve(defaultDimensions);
      };
      video.src = url;
      video.preload = "metadata";
    }
  });
};

// Hook to load dimensions for multiple items
export const useMultipleMediaDimensions = (data) => {
  const [itemDimensions, setItemDimensions] = useState(new Map());
  const [loadingDimensions, setLoadingDimensions] = useState(false);
  const itemDimensionsRef = useRef(itemDimensions);

  useEffect(() => {
    itemDimensionsRef.current = itemDimensions;
  }, [itemDimensions]);

  useEffect(() => {
    if (!data || data.length === 0) {
      const emptyMap = new Map();
      itemDimensionsRef.current = emptyMap;
      setItemDimensions(emptyMap);
      setLoadingDimensions(false);
      return;
    }

    let cancelled = false;

    const loadDimensions = async () => {
      setLoadingDimensions(true);
      const currentDimensions = new Map(itemDimensionsRef.current);
      let hasSyncUpdates = false;

      data.forEach((item) => {
        if (!item?.url) return;
        if (currentDimensions.has(item.url)) return;
        if (dimensionsCache.has(item.url)) {
          currentDimensions.set(item.url, dimensionsCache.get(item.url));
          hasSyncUpdates = true;
        }
      });

      if (hasSyncUpdates && !cancelled) {
        const updatedMap = new Map(currentDimensions);
        itemDimensionsRef.current = updatedMap;
        setItemDimensions(updatedMap);
      }

      const pendingItems = data.filter(
        (item) =>
          item &&
          item.url &&
          !currentDimensions.has(item.url) &&
          !dimensionsCache.has(item.url)
      );

      if (pendingItems.length === 0) {
        setLoadingDimensions(false);
        return;
      }

      const getItemDimensions = async (item) => {
        if (dimensionsCache.has(item.url)) {
          return dimensionsCache.get(item.url);
        }

        return new Promise((resolve) => {
          const isImage = isLikelyImageUrl(item.url);

          if (isImage) {
            const img = new Image();
            img.onload = () => {
              if (cancelled) return;
              const dimensions = {
                width: img.naturalWidth,
                height: img.naturalHeight,
              };
              dimensionsCache.set(item.url, dimensions);
              resolve(dimensions);
            };
            img.onerror = () => {
              if (cancelled) return;
              const defaultDimensions = { width: 1080, height: 1080 };
              dimensionsCache.set(item.url, defaultDimensions);
              resolve(defaultDimensions);
            };
            img.src = item.url;
          } else {
            const video = document.createElement("video");
            video.onloadedmetadata = () => {
              if (cancelled) return;
              const dimensions = {
                width: video.videoWidth,
                height: video.videoHeight,
              };
              dimensionsCache.set(item.url, dimensions);
              resolve(dimensions);
            };
            video.onerror = () => {
              if (cancelled) return;
              const defaultDimensions = { width: 1080, height: 1920 };
              dimensionsCache.set(item.url, defaultDimensions);
              resolve(defaultDimensions);
            };
            video.src = item.url;
            video.preload = "metadata";
          }
        });
      };

      const batchSize = 20;
      const totalBatches = Math.ceil(pendingItems.length / batchSize);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        if (cancelled) break;

        const startIndex = batchIndex * batchSize;
        const endIndex = Math.min(startIndex + batchSize, pendingItems.length);
        const batch = pendingItems.slice(startIndex, endIndex);

        const promises = batch.map(async (item) => {
          if (cancelled) return;
          try {
            const dimensions = await getItemDimensions(item);
            if (!cancelled) {
              currentDimensions.set(item.url, dimensions);
            }
          } catch (error) {
            if (!cancelled) {
              console.error("Error loading dimensions for", item.url, error);
              currentDimensions.set(item.url, { width: 1080, height: 1080 });
            }
          }
        });

        await Promise.all(promises);

        if (!cancelled) {
          const updatedMap = new Map(currentDimensions);
          itemDimensionsRef.current = updatedMap;
          setItemDimensions(updatedMap);
        }

        if (batchIndex < totalBatches - 1) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      if (!cancelled) {
        setLoadingDimensions(false);
      }
    };

    loadDimensions();

    return () => {
      cancelled = true;
    };
  }, [data]);

  return { itemDimensions, loadingDimensions };
};
